import type { Auth0ContextInterface } from "@auth0/auth0-react";

const appServicesAuthTokenUrl = ".auth/me";
const appServicesAuthTokenRefreshUrl = ".auth/refresh";
const appServicesAuthLogoutUrl = ".auth/logout";

interface AppServicesToken {
    id_token: string;
    access_token: string;
    user_claims: Record<string, any>;
    expires_on: string;
}

interface Auth0Config {
    domain?: string;
    clientId?: string;
    audience?: string;
    logoutUrl?: string | null;
    oidClaim?: string;
    organizationClaim?: string;
}

interface AuthSetup {
    useLogin: boolean;
    requireAccessControl: boolean;
    enableUnauthenticatedAccess: boolean;
    auth0Config?: Auth0Config;
}

interface GetTokenOptions {
    requireValidToken?: boolean;
    triggerLoginRedirectOnFailure?: boolean;
}

async function fetchAuthSetup(): Promise<AuthSetup> {
    const response = await fetch("/auth_setup");
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    const setup = (await response.json()) as AuthSetup;
    console.log("[AuthConfig] Loaded auth setup", setup);
    return setup;
}

// Remove top-level await - export async initialization function instead
let authSetupPromise: Promise<AuthSetup> | null = null;
let authSetupCache: AuthSetup | null = null;

export async function initializeAuthSetup(): Promise<AuthSetup> {
    if (authSetupCache) {
        return authSetupCache;
    }
    if (!authSetupPromise) {
        authSetupPromise = fetchAuthSetup();
    }
    authSetupCache = await authSetupPromise;
    return authSetupCache;
}

export function getAuthSetup(): AuthSetup | null {
    return authSetupCache;
}

// Lazy getters that throw if auth setup not initialized
export function getUseLogin(): boolean {
    if (!authSetupCache) {
        throw new Error("Auth setup not initialized. Call initializeAuthSetup() first.");
    }
    return authSetupCache.useLogin;
}

export function getRequireAccessControl(): boolean {
    if (!authSetupCache) {
        throw new Error("Auth setup not initialized. Call initializeAuthSetup() first.");
    }
    return authSetupCache.requireAccessControl;
}

export function getEnableUnauthenticatedAccess(): boolean {
    if (!authSetupCache) {
        throw new Error("Auth setup not initialized. Call initializeAuthSetup() first.");
    }
    return authSetupCache.enableUnauthenticatedAccess;
}

export function getRequireLogin(): boolean {
    const useLogin = getUseLogin();
    const enableUnauth = getEnableUnauthenticatedAccess();
    return useLogin && !enableUnauth;
}

export function getAuth0Config(): Auth0Config {
    if (!authSetupCache) {
        throw new Error("Auth setup not initialized. Call initializeAuthSetup() first.");
    }
    return authSetupCache.auth0Config ?? {};
}

// Export compatibility constants (will be set after initialization)
export let useLogin = false;
export let requireAccessControl = false;
export let enableUnauthenticatedAccess = false;
export let requireLogin = false;
export let auth0Config: Auth0Config = {};

// Internal function to update exports after initialization
export function _updateExportsAfterInit(setup: AuthSetup) {
    useLogin = setup.useLogin;
    requireAccessControl = setup.requireAccessControl;
    enableUnauthenticatedAccess = setup.enableUnauthenticatedAccess;
    requireLogin = useLogin && !enableUnauthenticatedAccess;
    auth0Config = setup.auth0Config ?? {};
}

declare global {
    // Cache the app services token if it's available
    var cachedAppServicesToken: AppServicesToken | null;
}
globalThis.cachedAppServicesToken = null;

const checkNotExpired = (appServicesToken: AppServicesToken) => {
    const currentDate = new Date();
    const expiresOnDate = new Date(appServicesToken.expires_on);
    return expiresOnDate > currentDate;
};

const getAppServicesToken = (): Promise<AppServicesToken | null> => {
    if (globalThis.cachedAppServicesToken && checkNotExpired(globalThis.cachedAppServicesToken)) {
        console.log("[AuthConfig] Using cached App Services token", globalThis.cachedAppServicesToken);
        return Promise.resolve(globalThis.cachedAppServicesToken);
    }

    const getAppServicesTokenFromMe: () => Promise<AppServicesToken | null> = () => {
        return fetch(appServicesAuthTokenUrl).then(r => {
            if (r.ok) {
                return r.json().then(json => {
                    if (json.length > 0) {
                        const token = {
                            id_token: json[0]["id_token"] as string,
                            access_token: json[0]["access_token"] as string,
                            user_claims: json[0]["user_claims"].reduce((acc: Record<string, any>, item: Record<string, any>) => {
                                acc[item.typ] = item.val;
                                return acc;
                            }, {}) as Record<string, any>,
                            expires_on: json[0]["expires_on"] as string
                        } as AppServicesToken;
                        console.log("[AuthConfig] Auth0 ID token (raw)", token.id_token);
                        console.log("[AuthConfig] Auth0 access token (raw)", token.access_token);
                        console.log("[AuthConfig] Retrieved App Services token", token);
                        return token;
                    }

                    return null;
                });
            }

            return null;
        });
    };

    return getAppServicesTokenFromMe().then(token => {
        if (token) {
            if (checkNotExpired(token)) {
                globalThis.cachedAppServicesToken = token;
                console.log("[AuthConfig] Cached App Services token", token);
                return token;
            }

            return fetch(appServicesAuthTokenRefreshUrl).then(r => {
                if (r.ok) {
                    console.log("[AuthConfig] Refreshing App Services token");
                    return getAppServicesTokenFromMe();
                }
                return null;
            });
        }

        return null;
    });
};

// Remove top-level await - make this a lazy-evaluated async function
let isUsingAppServicesLoginCache: boolean | null = null;

export async function checkIsUsingAppServicesLogin(): Promise<boolean> {
    if (isUsingAppServicesLoginCache !== null) {
        return isUsingAppServicesLoginCache;
    }
    const token = await getAppServicesToken();
    isUsingAppServicesLoginCache = token !== null;
    return isUsingAppServicesLoginCache;
}

export function getIsUsingAppServicesLogin(): boolean {
    return isUsingAppServicesLoginCache ?? false;
}

// Export for backward compatibility
export let isUsingAppServicesLogin = false;

export function _updateIsUsingAppServicesLogin(value: boolean) {
    isUsingAppServicesLogin = value;
    isUsingAppServicesLoginCache = value;
}

export const appServicesLogout = (postLogoutRedirectUri: string) => {
    const logoutUrl = new URL(appServicesAuthLogoutUrl, window.location.origin);
    logoutUrl.searchParams.set("post_logout_redirect_uri", postLogoutRedirectUri);
    window.location.replace(logoutUrl.toString());
};

const getAppServicesUsername = async (): Promise<string | null> => {
    const appServicesToken = await getAppServicesToken();
    if (appServicesToken?.user_claims) {
        return appServicesToken.user_claims.preferred_username ?? appServicesToken.user_claims.name ?? appServicesToken.user_claims.email ?? null;
    }
    return null;
};

export const checkLoggedIn = async (authContext: Auth0ContextInterface<Record<string, unknown>> | undefined): Promise<boolean> => {
    if (authContext?.isAuthenticated) {
        try {
            const token = await getToken(authContext, {
                requireValidToken: true,
                triggerLoginRedirectOnFailure: false
            });
            return Boolean(token);
        } catch {
            return false;
        }
    }
    const appServicesToken = await getAppServicesToken();
    return appServicesToken !== null;
};

/**
 * Auth0 error codes that indicate the user needs to re-authenticate.
 * These errors occur when the refresh token is expired, revoked, or invalid.
 */
const REAUTH_REQUIRED_ERRORS = [
    "login_required",
    "invalid_grant",
    "consent_required",
    "interaction_required",
    "missing_refresh_token"
];

export class ReauthenticationRequiredError extends Error {
    constructor(message = "Your session has expired. Please sign in again.") {
        super(message);
        this.name = "ReauthenticationRequiredError";
    }
}

let isReauthRedirectInProgress = false;

/**
 * Check if an Auth0 error requires re-authentication.
 *
 * :param error: The error object from Auth0 SDK
 * :returns: True if the user needs to re-authenticate
 */
const isReauthRequiredError = (error: unknown): boolean => {
    if (!error || typeof error !== "object") {
        return false;
    }
    const errorCode = (error as { error?: string }).error;
    return typeof errorCode === "string" && REAUTH_REQUIRED_ERRORS.includes(errorCode);
};

const decodeJwtClaims = (token: string): Record<string, unknown> | null => {
    const parts = token.split(".");
    if (parts.length !== 3) {
        return null;
    }

    try {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
        const payload = atob(padded);
        return JSON.parse(payload) as Record<string, unknown>;
    } catch {
        return null;
    }
};

const getMissingRequiredClaims = (token: string): string[] => {
    const claims = decodeJwtClaims(token);
    if (!claims) {
        return ["jwt_payload"];
    }

    const requiredClaimKeys = [auth0Config.oidClaim, auth0Config.organizationClaim]
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0);

    const missingClaims: string[] = [];
    for (const claimKey of requiredClaimKeys) {
        const value = claims[claimKey];
        if (typeof value === "string") {
            if (value.trim().length === 0) {
                missingClaims.push(claimKey);
            }
            continue;
        }
        if (value === undefined || value === null) {
            missingClaims.push(claimKey);
        }
    }

    return missingClaims;
};

const triggerReauthenticationRedirect = (
    authContext: Auth0ContextInterface<Record<string, unknown>>,
    errorCode: string,
    errorDescription: string
) => {
    if (isReauthRedirectInProgress) {
        return;
    }

    isReauthRedirectInProgress = true;
    console.warn("[AuthConfig] Session expired or invalid. Redirecting to login.", {
        errorCode,
        errorDescription
    });

    void authContext
        .loginWithRedirect({
            appState: { returnTo: window.location.pathname }
        })
        .catch(redirectError => {
            console.error("[AuthConfig] Failed to start re-authentication redirect", redirectError);
        })
        .finally(() => {
            isReauthRedirectInProgress = false;
        });
};

export const getToken = async (
    authContext: Auth0ContextInterface<Record<string, unknown>> | undefined,
    options: GetTokenOptions = {}
): Promise<string | undefined> => {
    const requireValidToken = options.requireValidToken ?? false;
    const triggerLoginRedirectOnFailure = options.triggerLoginRedirectOnFailure ?? true;
    const appServicesToken = await getAppServicesToken();
    if (appServicesToken) {
        console.log("[AuthConfig] Returning App Services access token", appServicesToken.access_token);
        return Promise.resolve(appServicesToken.access_token);
    }

    if (!authContext) {
        if (requireValidToken) {
            throw new ReauthenticationRequiredError("Authentication context is not available.");
        }
        return undefined;
    }

    try {
        const tokenRequestOptions: {
            authorizationParams?: { audience: string };
            cacheMode?: "on" | "off" | "cache-only";
        } = {};

        if (auth0Config.audience) {
            tokenRequestOptions.authorizationParams = {
                audience: auth0Config.audience
            };
        }

        if (requireValidToken) {
            // Bypass cache during validation so stale local tokens don't pass chat requests.
            tokenRequestOptions.cacheMode = "off";
        }

        const token = await authContext.getAccessTokenSilently(tokenRequestOptions);
        console.log(
            auth0Config.audience
                ? "[AuthConfig] Retrieved Auth0 access token (with audience)"
                : "[AuthConfig] Retrieved Auth0 access token",
            token
        );

        if (requireValidToken) {
            const missingClaims = getMissingRequiredClaims(token);
            if (missingClaims.length > 0) {
                const claimList = missingClaims.join(", ");
                console.error("[AuthConfig] Access token missing required claims", {
                    missingClaims,
                    timestamp: new Date().toISOString()
                });

                if (triggerLoginRedirectOnFailure) {
                    triggerReauthenticationRedirect(
                        authContext,
                        "missing_required_claims",
                        `Token missing required claims: ${claimList}`
                    );
                }
                throw new ReauthenticationRequiredError(
                    "Your session is missing required access claims. Please sign in again."
                );
            }
        }

        return token;
    } catch (error: unknown) {
        const errorObj = error as { error?: string; error_description?: string; message?: string };
        const errorCode = errorObj?.error || "unknown_error";
        const errorDescription = errorObj?.error_description || errorObj?.message || "No description available";

        console.error("[AuthConfig] Failed to get access token silently", {
            error: errorCode,
            description: errorDescription,
            timestamp: new Date().toISOString()
        });

        const shouldReauthenticate = isReauthRequiredError(error) || requireValidToken;
        if (shouldReauthenticate) {
            if (triggerLoginRedirectOnFailure) {
                triggerReauthenticationRedirect(authContext, errorCode, errorDescription);
            }
            if (requireValidToken) {
                throw new ReauthenticationRequiredError();
            }
            return undefined;
        }

        return undefined;
    }
};

export const getUsername = async (authContext: Auth0ContextInterface<Record<string, unknown>> | undefined): Promise<string | null> => {
    if (authContext?.user) {
        const { name, email, nickname } = authContext.user as Record<string, string | undefined>;
        return name ?? email ?? nickname ?? null;
    }
    return getAppServicesUsername();
};

export const getTokenClaims = async (authContext: Auth0ContextInterface<Record<string, unknown>> | undefined): Promise<Record<string, unknown> | undefined> => {
    if (authContext) {
        try {
            const claims = await authContext.getIdTokenClaims();
            if (claims) {
                const rawIdToken = (claims as { __raw?: string }).__raw;
                if (rawIdToken) {
                    console.log("[AuthConfig] Retrieved Auth0 ID token (raw)", rawIdToken);
                }
                console.log("[AuthConfig] Retrieved Auth0 ID token claims", claims);
                return claims;
            }
        } catch (error: unknown) {
            const errorObj = error as { error?: string; error_description?: string; message?: string };
            const errorCode = errorObj?.error || "unknown_error";
            const errorDescription = errorObj?.error_description || errorObj?.message || "No description available";

            console.error("[AuthConfig] Failed to get ID token claims", {
                error: errorCode,
                description: errorDescription,
                timestamp: new Date().toISOString()
            });

            if (isReauthRequiredError(error)) {
                triggerReauthenticationRedirect(authContext, errorCode, errorDescription);
                return undefined;
            }
        }
    }
    const appServicesToken = await getAppServicesToken();
    if (appServicesToken?.user_claims) {
        console.log("[AuthConfig] Using App Services user claims", appServicesToken.user_claims);
    }
    return appServicesToken?.user_claims;
};
